import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import { custApi } from "../../lib/customerApi";
import {
  House, Recycle, Heartbeat, HandHeart, User,
  Bell, ChatCircleDots, SignOut, CaretRight,
  Receipt, Coins, Ticket, Gear, Wallet, Lock,
} from "@phosphor-icons/react";
import { useCustAuth } from "../../lib/customerAuth";
import { useCustomerCms } from "../../lib/useCustomerCms";
import MeReminderAlarm from "./MeReminderAlarm";
import LocationUpdatePrompt from "./LocationUpdatePrompt";
import LocationGate from "../../lib/LocationGate";

/**
 * Rescigo Health — customer shell (2026 nav revamp)
 * Bottom: Home · Hygiene · Health · Daily Care
 * Top   : Alerts · Profile-menu trigger
 */

const BOTTOM_TABS = [
  { key: "home",    to: "/me",         label: "Home",       icon: House,     end: true },
  { key: "hygiene", to: "/me/hygiene", label: "Hygiene",    icon: Recycle },
  { key: "health",  to: "/me/health",  label: "Health",     icon: Heartbeat },
  { key: "care",    to: "/me/care",    label: "Daily Care", icon: HandHeart },
];

const PROFILE_MENU = [
  { key: "menu_profile",  to: "/me/profile",       label: "My Profile",    icon: User,     note: "Address, contact, preferences" },
  { key: "menu_care",     to: "/me/customer-care", label: "Customer care", icon: Ticket,   note: "Tickets & our replies" },
  { key: "menu_chat",     to: "/me/chat",          label: "Chat",          icon: ChatCircleDots, note: "Chat with Rigo" },
  { key: "menu_dues",     to: "/me/dues",          label: "Dues",          icon: Receipt,  note: "Invoices & payments" },
  { key: "menu_wallet",   to: "/me/wallet",        label: "Wallet",        icon: Wallet,   note: "Balance & recharges" },
  { key: "menu_settings", to: "/me/settings",      label: "Settings",      icon: Gear,     note: "Notifications & privacy" },
];

const OPEN_DRAWER_EVENT = "me:open-drawer";
export const openMeDrawer = () => window.dispatchEvent(new CustomEvent(OPEN_DRAWER_EVENT));

export default function MeLayout() {
  const { customer, ready, logout } = useCustAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const nav = useNavigate();
  const location = useLocation();
  // Iter 267 — record screen views + time on screen for AI Service Analytics.
  useEffect(() => {
    const screen = location.pathname;
    const start = Date.now();
    if (!localStorage.getItem("rescigo_cust_token")) return undefined;
    custApi.post("/me/app-events", { type: "screen", screen }).catch(() => {});
    return () => {
      const ms = Date.now() - start;
      if (ms > 1000) custApi.post("/me/app-events", { type: "action", screen, duration_ms: Math.min(ms, 3600000) }).catch(() => {});
    };
  }, [location.pathname]);
  const cms = useCustomerCms();
  // Iter 205 · auto-hide chat FAB while the customer is scrolling so
  // it never sits on top of price / action buttons on smaller screens.
  // Shows again ~300ms after scroll settles.
  const [fabHidden, setFabHidden] = useState(false);
  useEffect(() => {
    let idleTimer;
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      // Hide immediately while actively scrolling.
      if (Math.abs(y - lastY) > 6) setFabHidden(true);
      lastY = y;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setFabHidden(false), 350);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(idleTimer);
    };
  }, []);

  const visibleTabs = cms.orderPages(
    BOTTOM_TABS.filter(t => cms.isPageVisible(t.key)),
    (t) => t.key,
  );
  // Iter 220 — mark tabs that require a verified account so we can
  // render a lock badge on the bottom-nav for pending / rejected users.
  const custStatus = (customer?.status || "").toLowerCase();
  const isUnverified = custStatus && custStatus !== "active";
  const LOCKED_KEYS = new Set(["hygiene"]);
  const visibleMenu = cms.orderFeatures(
    "profile",
    PROFILE_MENU.filter(m => cms.isFeatureVisible("profile", m.key)),
    (m) => m.key,
  );

  // Presence heartbeat
  useEffect(() => {
    if (!customer) return;
    const beat = () => {
      if (document.hidden) return;
      custApi.post("/me/presence", { page: location.pathname }).catch(() => {});
    };
    beat();
    const iv = setInterval(beat, 30000);
    const onVis = () => { if (!document.hidden) beat(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [customer, location.pathname]);

  // Global drawer open listener
  useEffect(() => {
    const onOpen = () => setDrawerOpen(true);
    window.addEventListener(OPEN_DRAWER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_DRAWER_EVENT, onOpen);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Iter 220f — always land at the top of the page when navigating
  // between customer routes. Avoids the "middle of a long page"
  // surprise after tapping a nav item or the browser back button.
  useEffect(() => {
    // `instant` sidesteps the smooth-scroll spring so the top is
    // ready by the time the new page paints.
    try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }
    catch { window.scrollTo(0, 0); }
  }, [location.pathname]);

  // Pull unread alert count (chat/notifications). Non-critical; hush errors.
  useEffect(() => {
    if (!customer) return;
    const load = () =>
      custApi.get("/me/chat/notify-poll")
        .then(r => setAlertCount(Number(r.data?.unread || 0)))
        .catch(() => {});
    load();
    const iv = setInterval(load, 45000);
    return () => clearInterval(iv);
  }, [customer]);

  if (!ready) return null;
  if (!customer) return <Navigate to="/me/login" replace />;

  const handleLogout = () => {
    setDrawerOpen(false);
    logout?.();
    nav("/me/login", { replace: true });
  };
  const initials = (customer.name || "?").trim().split(/\s+/)
                     .slice(0, 2).map(s => s[0]?.toUpperCase()).join("");

  return (
    <LocationGate
      pingPath="/api/me/location/ping"
      tokenKey="rescigo_cust_token"
      bodyShape="customer"
      label="Rescigo Health"
      onRevoked={() => { try { logout?.(); } catch { /* noop */ } }}
    >
    <div className="min-h-[100dvh] bg-white font-body text-[#111827]" data-testid="me-shell">
      {/* Centered mobile frame */}
      <div className="mx-auto max-w-[460px] min-h-[100dvh] bg-white shadow-[0_0_60px_rgba(220,38,38,0.06)] relative flex flex-col overflow-x-hidden">

        {/* ── Global Top Bar ── */}
        <header
          className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gray-50 px-5 py-3 flex items-center gap-2"
          data-testid="me-topbar"
        >
          <Link to="/me" className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-white ring-1 ring-red-100 flex items-center justify-center shrink-0 shadow-sm">
              <img src="/brand/rescigo-mark-128.png" alt="Rescigo" className="w-7 h-7 object-contain"/>
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-[15px] leading-none tracking-tight">
                <span className="text-[#111827]">rescigo</span>
                <span className="text-[#DC2626] ml-0.5">health</span>
              </div>
              <div className="text-[9px] text-gray-400 mt-0.5 tracking-wide truncate">Health &amp; Waste, Handled Right</div>
            </div>
          </Link>
          <Link
            to="/me/customer-care"
            data-testid="me-alerts-btn"
            className="relative w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Alerts"
          >
            <Bell size={18} weight="duotone" className="text-[#111827]"/>
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                {alertCount > 9 ? "9+" : alertCount}
              </span>
            )}
          </Link>
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="me-profile-btn"
            className="w-10 h-10 rounded-full bg-gradient-to-br from-[#DC2626] to-[#7F1D1D] text-white font-display font-bold text-sm flex items-center justify-center shadow-md active:scale-95 transition-transform overflow-hidden ring-2 ring-red-100"
            aria-label="Open profile menu"
          >
            {initials || <User size={16}/>}
          </button>
        </header>

        <main className="flex-1 pb-44 px-0 overflow-x-hidden" data-testid="me-main">
          <div className="min-w-0">
            <Outlet />
          </div>
        </main>

        {/* Global reminder alarm engine */}
        <MeReminderAlarm/>

        {/* Iter 177 — location update prompt (shows once per session
            if the customer's saved coords are missing / (0,0) / off-country) */}
        <LocationUpdatePrompt/>

        {/* ── Floating chat FAB (hidden on /chat, auto-hides while scrolling) ── */}
        {!location.pathname.startsWith("/me/chat") && (
          <Link
            to="/me/chat"
            data-testid="me-chat-fab"
            aria-hidden={fabHidden}
            className={`fixed z-30 bottom-28 right-[calc(50%-215px)] max-[500px]:right-3
                       w-12 h-12 rounded-full bg-gradient-to-br from-[#DC2626] to-[#B91C1C]
                       text-white flex items-center justify-center
                       shadow-[0_10px_30px_rgba(220,38,38,0.35)] ring-2 ring-white/90
                       active:scale-95 transition-all duration-200 ${
                         fabHidden
                           ? "opacity-0 translate-y-2 pointer-events-none"
                           : "opacity-100 translate-y-0"
                       }`}
            title="Chat with Rigo">
            <ChatCircleDots size={22} weight="fill"/>
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#86D67D] ring-2 ring-white animate-pulse"/>
          </Link>
        )}

        {/* ── Bottom nav (4 tabs — Home / Hygiene / Health / Daily Care) ── */}
        <nav
          className="fixed z-40 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[460px]
                     backdrop-blur-xl bg-white/95 border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]"
          data-testid="me-nav">
          <div className={`grid`} style={{ gridTemplateColumns: `repeat(${Math.max(1, visibleTabs.length)}, minmax(0, 1fr))` }}>
            {visibleTabs.map((t) => {
              const locked = isUnverified && LOCKED_KEYS.has(t.key);
              return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`me-tab-${t.label.toLowerCase().replace(/\s+/g,"-")}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 pt-3 pb-2 transition-colors relative ${
                    isActive ? "text-[#DC2626]" : locked ? "text-gray-300" : "text-gray-400"
                  }`
                }>
                {({ isActive }) => (
                  <>
                    <span className="relative">
                      <t.icon size={22} weight={isActive ? "fill" : "regular"}/>
                      {locked && (
                        <span
                          data-testid={`me-tab-lock-${t.key}`}
                          className="absolute -top-1 -right-2 w-4 h-4 rounded-full bg-[#F59E0B] text-white flex items-center justify-center ring-2 ring-white"
                          title="Verification required"
                        >
                          <Lock size={9} weight="fill"/>
                        </span>
                      )}
                    </span>
                    <span className={`text-[10px] font-semibold tracking-wide ${
                      isActive ? "text-[#DC2626]" : locked ? "text-gray-400" : "text-gray-500"
                    }`}>{t.label}</span>
                    {isActive && (
                      <span className="absolute bottom-1 w-6 h-0.5 rounded-full bg-[#DC2626]"/>
                    )}
                  </>
                )}
              </NavLink>
              );
            })}
          </div>
          <div className="h-[env(safe-area-inset-bottom)]"/>
        </nav>

        {/* ── Slide-in profile drawer ── */}
        {drawerOpen && (
          <div
            className="fixed z-50 inset-0 bg-black/40 flex justify-start"
            onClick={() => setDrawerOpen(false)}
            data-testid="me-drawer-scrim">
            <div
              className="w-[86%] max-w-[380px] bg-white h-full overflow-y-auto animate-slideIn"
              onClick={(e) => e.stopPropagation()}
              data-testid="me-drawer">
              <div className="px-5 pt-6 pb-5 bg-gradient-to-br from-[#DC2626] to-[#991B1B] text-white">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-white/10 ring-1 ring-white/20 font-display font-bold text-xl flex items-center justify-center">
                    {initials || <User size={20}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight truncate">{customer.name}</div>
                    {customer.phone && (
                      <div className="text-[12px] text-white/70 font-mono">{customer.phone}</div>
                    )}
                    {customer.customer_code && (
                      <div className="mt-1 inline-block text-[10px] uppercase tracking-widest font-bold bg-white/10 rounded-full px-2 py-0.5">
                        {customer.customer_code}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="py-2">
                {visibleMenu.map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`me-menu-${item.label.toLowerCase().replace(/\s+/g,"-")}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-[#FEF2F2] transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
                      <item.icon size={18} weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-[#111827]">{item.label}</div>
                      <div className="text-[11px] text-gray-500 truncate">{item.note}</div>
                    </div>
                    <CaretRight size={12} className="text-gray-400"/>
                  </Link>
                ))}
                <div className="mx-5 my-2 border-t border-gray-100"/>
                {cms.isFeatureVisible("profile", "menu_rewards") && (
                <Link
                  to="/me/affiliate"
                  onClick={() => setDrawerOpen(false)}
                  data-testid="me-menu-rewards"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[#FEF2F2] transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
                    <Coins size={18} weight="duotone"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[#111827]">Rewards</div>
                    <div className="text-[11px] text-gray-500 truncate">Refer & earn</div>
                  </div>
                  <CaretRight size={12} className="text-gray-400"/>
                </Link>
                )}
                <div className="mx-5 my-2 border-t border-gray-100"/>
                <button
                  onClick={handleLogout}
                  data-testid="me-menu-logout"
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#FEF2F2] transition-colors text-[#DC2626]">
                  <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] flex items-center justify-center shrink-0">
                    <SignOut size={18} weight="duotone"/>
                  </div>
                  <div className="text-[14px] font-semibold">Sign out</div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .animate-slideIn { animation: slideIn 0.22s ease-out; }
      `}</style>
    </div>
    </LocationGate>
  );
}
